/**
 * `<page-playground>` — the SQL playground, **dry run only**, routed at
 * `/instances/:id/playground`.
 *
 * Paste a statement, choose a subject, and see what the published policies would
 * do to it (`SC-F712`, built on `SC-F704`'s proxied `policy explain` and
 * `SC-F332`'s job). The statement is parsed by Sluice's own parser inside a
 * one-shot container on the instance's pinned image, so a shape- or CEL-dependent
 * policy behaves exactly as it would on the live path.
 *
 * Stories: `SC-S714`, and `SC-S809`'s reproduce action, which arrives here with a
 * statement and a generation prefilled.
 *
 * **Nothing is executed.** No datasource is contacted, no rows exist, and there
 * is no element on this page that returns data — not a disabled one either.
 * Live execution (`SC-F713`) and subject impersonation (`SC-F714`) are v1: they
 * need a feature grant absent from the default role set, a step-up grant, refusal
 * for every scoped token and a control-plane audit record on every use, and
 * `06` §3.10 keeps that route out of the scoped-token map by *absence*. So this
 * page offers no affordance suggesting the capability exists, and the API client
 * carries no function for it. The distinction the split rests on: **explaining as
 * a synthetic subject returns a decision; executing as a subject returns rows.**
 *
 * Two limitations are badged rather than buried. The subject is always
 * JWT-shaped, so a `SubjectBinding` that only accepts API keys cannot be
 * simulated at all and is offered disabled with that reason (`SLUICE-U27`). And a
 * post-query mask on a column this statement touches is a live failure mode the
 * dry run cannot observe — explain returns no rewrite effect — so the condition
 * is stated instead of being silently absent (`SC-F516`).
 */

import { LitElement, css, html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
import type {
  BnBadgeVariant,
  BnColumn,
  BnKvRow,
  BnRow,
  BnSelectChangeDetail,
  BnSelectOption,
} from "../components/index.js";

import {
  explain,
  explainPrefill,
  parseExplainPrefill,
  type DecisionKind,
  type ExplainResult,
  type SyntheticSubject,
  type ValidationTarget,
} from "../api/cli.js";
import { ApiError, parseError, validationFields } from "../api/errors.js";
import { getInstance, type Instance } from "../api/instances.js";
import { cancelJob, isTerminal, jobFailure, watchJob, type Job } from "../api/jobs.js";
import {
  getPolicy,
  listPolicies,
  type PolicyKind,
  type PolicyObject,
  type PolicyRow,
} from "../api/policies.js";
import { hrefFor, navigate } from "../nav.js";

const WAIT_SECONDS = 20;

/** How many objects of one kind this page will re-read for its convenience
 * annotations. A draft with more subject bindings or column masks than this is
 * annotated from the first page of each — the annotations are a convenience and
 * the run is the product, so the read is bounded rather than unbounded. */
const DRAFT_OBJECT_CAP = 50;

/** Every explain-shaped surface carries it (`SLUICE-U27`). */
const JWT_ONLY_BADGE = "JWT subject semantics only";

const TARGET_OPTIONS: BnSelectOption[] = [
  {
    value: "draft",
    label: "Draft",
    description: "What would happen if I published this. The default, and what the editors change.",
  },
  { value: "live", label: "Live generation", description: "What is happening now." },
  {
    value: "generation",
    label: "A generation number",
    description: "The bundle that was live at the time, for reproducing an old decision.",
  },
];

/** The honest reading of the effective decision, in the playground's register:
 * the author is asking "what would my policies do to this query". */
const DECISIONS: Record<DecisionKind, { label: string; variant: BnBadgeVariant; headline: string }> =
  {
    allow: {
      label: "Allow",
      variant: "allow",
      headline: "This statement would run, with the filters and masks below applied.",
    },
    no_policy_allowed: {
      label: "Deny",
      variant: "deny",
      headline: "No policy allowed this — default-deny, with nothing objecting.",
    },
    denied_by_policy: {
      label: "Deny",
      variant: "deny",
      headline: "A policy denied this, and the deny short-circuited every filter and mask.",
    },
    rejected: {
      label: "Reject",
      variant: "deny",
      headline: "A rejection rule refused this statement.",
    },
    unknown: {
      label: "Unknown",
      variant: "warning",
      headline: "The engine reported an outcome this build does not recognise.",
    },
  };

const MASK_COLUMNS: BnColumn[] = [
  { key: "column", label: "Column", mono: true },
  { key: "mask_type", label: "Mask" },
  { key: "policy", label: "From policy" },
  { key: "stage", label: "Stage" },
];

const POLICY_COLUMNS: BnColumn[] = [
  { key: "role", label: "Role in the decision", width: "1px" },
  { key: "kind", label: "Kind" },
  { key: "name", label: "Name" },
  { key: "priority", label: "Priority", width: "1px", align: "right" },
];

/** What a chosen binding tells us, read off its stored spec. */
interface BindingFacts {
  name: string;
  issuer: string;
  /** True when the binding declares any JWT verification material. */
  jwt: boolean;
  /** True when the binding declares API keys — the mode explain cannot model. */
  apiKeys: boolean;
  /** The claim paths the binding maps, by field name. */
  claimPaths: Record<string, string>;
}

/**
 * `<page-playground>` is the dry-run SQL playground: a statement, a subject, and
 * the decision your policies would reach.
 *
 * Implements `SC-F712` (dry run, built on `SC-F704`'s proxied explain) and
 * `SC-F332` (the job). It deliberately implements NO part of `SC-F713` (live
 * execution) or `SC-F714` (subject impersonation) and carries no affordance for
 * them, and it surfaces `SC-F516`'s post-query-mask failure mode, which a dry run
 * cannot observe for itself.
 *
 * Stories: `SC-S714`, `SC-S809`'s reproduce action.
 *
 *   <page-playground .instanceId=${id}></page-playground>
 */
@customElement("page-playground")
export class PagePlayground extends LitElement {
  @property() instanceId = "";

  @state() private instance: Instance | null = null;
  @state() private loading = true;
  @state() private loadError: ApiError | null = null;

  @state() private targetKind: "draft" | "live" | "generation" = "draft";
  @state() private generation = "";
  @state() private sql = "";
  @state() private bindingName = "";
  @state() private user = "";
  @state() private issuer = "";
  @state() private email = "";
  @state() private groupsText = "";
  @state() private fieldErrors: Record<string, string> = {};

  /** The draft's SubjectBinding objects, for the subject picker. Best-effort:
   * losing them costs the picker, not the page. */
  @state() private bindings: BindingFacts[] = [];
  /** ColumnMaskPolicy name → execution stage, computed server-side from the
   * stored arguments. It is how the post-query warning is derived rather than
   * guessed, since explain returns no mask arguments of its own. */
  @state() private maskStages: Record<string, string> = {};
  /** True once the draft lookups have been attempted, so "no bindings" and "not
   * loaded yet" are distinguishable. */
  @state() private draftRead = false;

  @state() private job: Job<ExplainResult> | null = null;
  @state() private result: ExplainResult | null = null;
  @state() private runError: ApiError | null = null;
  @state() private running = false;
  /** The target the result on screen was produced against, so a later switch of
   * the picker cannot relabel an old answer. */
  @state() private resultTarget: "draft" | "live" | "generation" = "draft";

  /** The statement the answer on screen was produced from, so an edited editor
   * cannot leave a decision on screen that describes text nobody can see. */
  @state() private ranSql = "";

  private abort?: AbortController;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    header {
      margin-bottom: var(--bino-space-lg);
    }
    .head-line {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
    }
    h2 {
      margin: 0 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-md);
      font-weight: 600;
    }
    .lede {
      margin: var(--bino-space-xs) 0 0;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      max-width: 80ch;
    }
    .stack {
      display: grid;
      gap: var(--bino-space-lg);
    }
    .row {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-end;
      gap: var(--bino-space-md);
    }
    .grid {
      display: grid;
      gap: var(--bino-space-md);
      grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr));
    }
    .row bn-field,
    .grid bn-field {
      margin-bottom: 0;
    }
    .narrow {
      width: 16rem;
    }
    fieldset {
      border: 1px solid var(--bino-border-light);
      border-radius: var(--bino-radius);
      padding: var(--bino-space-md);
      margin: var(--bino-space-md) 0;
    }
    legend {
      padding: 0 var(--bino-space-xs);
      font-size: var(--bino-font-size-sm);
      font-weight: 600;
      color: var(--bino-text-muted);
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
    p {
      margin: 0 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-md);
      max-width: 80ch;
    }
    p:last-child {
      margin-bottom: 0;
    }
    ul {
      margin: 0 0 var(--bino-space-sm);
      padding-left: var(--bino-space-lg);
      max-width: 80ch;
    }
    li {
      margin-bottom: var(--bino-space-xs);
      font-size: var(--bino-font-size-md);
    }
    .muted {
      color: var(--bino-text-secondary);
      font-size: var(--bino-font-size-sm);
    }
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
    .actions {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
    }
    .verdict {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
      margin-bottom: var(--bino-space-sm);
    }
    .verdict-line {
      font-size: var(--bino-font-size-lg);
      font-weight: 600;
    }
    .chips {
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-xs);
      margin-bottom: var(--bino-space-sm);
    }
    .chip {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      padding: 0.125rem var(--bino-space-sm);
      border-radius: var(--bino-radius-sm);
      background: var(--bino-surface-inset);
      color: var(--bino-text);
    }
    .section {
      margin-top: var(--bino-space-lg);
    }
    .section:first-of-type {
      margin-top: 0;
    }
    a.inline {
      color: var(--bino-primary);
      font-weight: 600;
      text-decoration: none;
    }
    a.inline:hover {
      text-decoration: underline;
    }
    a.inline:focus-visible {
      outline: none;
      border-radius: var(--bino-radius-sm);
      box-shadow: var(--bino-focus-ring-shadow);
    }
    .error-message {
      margin: 0 0 var(--bino-space-md);
      max-width: 70ch;
    }
    .error-actions {
      display: flex;
      gap: var(--bino-space-sm);
      margin-top: var(--bino-space-md);
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    this.applyPrefill();
    void this.load();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.abort?.abort();
  }

  override updated(changed: Map<string, unknown>) {
    if (changed.has("instanceId") && changed.get("instanceId") !== undefined) void this.load();
  }

  /** `?sql=`, `?subject=` and `?target=` are the same prefill contract the
   * explain page reads, so an audit record's reproduce action can open either. */
  private applyPrefill() {
    const pre = parseExplainPrefill(location.search);
    if (pre.target === "live" || pre.target === "draft") this.targetKind = pre.target;
    else if (typeof pre.target === "number") {
      this.targetKind = "generation";
      this.generation = String(pre.target);
    }
    if (pre.subject.user) this.user = pre.subject.user;
    if (pre.subject.issuer) this.issuer = pre.subject.issuer;
    if (pre.subject.email) this.email = pre.subject.email;
    if (pre.subject.groups) this.groupsText = pre.subject.groups.join(" ");
    if (pre.sql) this.sql = pre.sql;
  }

  private async load() {
    if (!this.instanceId) return;
    this.loading = true;
    try {
      this.instance = await getInstance(this.instanceId);
      this.loadError = null;
    } catch (err) {
      this.loadError = parseError(err);
      this.loading = false;
      return;
    }
    this.loading = false;
    void this.readDraft();
  }

  /**
   * The two draft reads this page enriches itself with. Both are best-effort and
   * independently fallible: a `validation`-scoped credential can run an explain
   * and cannot list policies, and losing either read costs a convenience rather
   * than the page.
   */
  private async readDraft() {
    const [bindings, masks] = await Promise.allSettled([
      listPolicies(this.instanceId, { kind: "SubjectBinding" }),
      listPolicies(this.instanceId, { kind: "ColumnMaskPolicy" }),
    ]);
    if (bindings.status === "fulfilled") {
      const objects = await this.readObjects("SubjectBinding", bindings.value.items);
      this.bindings = objects.map((p) => bindingFacts(p));
    }
    if (masks.status === "fulfilled") {
      const stages: Record<string, string> = {};
      for (const p of await this.readObjects("ColumnMaskPolicy", masks.value.items)) {
        const stage = p.derived.mask_stage;
        if (stage) stages[p.metadata.name] = stage;
      }
      this.maskStages = stages;
    }
    this.draftRead = true;
  }

  /**
   * readObjects turns list rows into full records.
   *
   * The list route answers `PolicyRow` — six columns and two badges — and it does
   * so deliberately: shipping every object's spec would make it the largest
   * response in the product. But this page needs two things that live only on the
   * full record: which authentication mode a `SubjectBinding` accepts (its spec),
   * and a `ColumnMaskPolicy`'s `derived.mask_stage`. So it re-reads them one by
   * one, bounded, and tolerates individual failures — a mask whose stage could not
   * be read is reported as an UNKNOWN stage rather than assumed to be the
   * SQL-pushdown one, which is the caveat `SC-S507` is about.
   */
  private async readObjects(kind: PolicyKind, rows: PolicyRow[]): Promise<PolicyObject[]> {
    const settled = await Promise.allSettled(
      rows.slice(0, DRAFT_OBJECT_CAP).map((r) => getPolicy(this.instanceId, kind, r.name)),
    );
    return settled.flatMap((s) => (s.status === "fulfilled" ? [s.value] : []));
  }

  /* ── the form ──────────────────────────────────────────────────────────── */

  private get target(): ValidationTarget {
    if (this.targetKind === "generation") return Number(this.generation);
    return this.targetKind;
  }

  private get binding(): BindingFacts | undefined {
    return this.bindings.find((b) => b.name === this.bindingName);
  }

  /** Groups are entered space-separated and never comma-separated, because the
   * CLI splits the flag on commas: offering a comma-separated field would invite
   * exactly the value the API refuses. */
  private get groups(): string[] {
    return this.groupsText.split(/\s+/).filter((g) => g !== "");
  }

  private get subject(): SyntheticSubject {
    const out: SyntheticSubject = { user: this.user.trim() };
    if (this.issuer.trim()) out.issuer = this.issuer.trim();
    if (this.email.trim()) out.email = this.email.trim();
    if (this.groups.length > 0) out.groups = this.groups;
    return out;
  }

  private validateForm(): Record<string, string> {
    const errs: Record<string, string> = {};
    if (this.user.trim() === "") {
      errs["subject.user"] = "Required: the dry run evaluates one named subject.";
    }
    if (this.sql.trim() === "") errs["sql"] = "Paste a statement to evaluate.";
    for (const g of this.groups) {
      if (g.includes(",")) {
        errs["subject.groups"] =
          "A group may not contain a comma — the CLI splits the flag on commas. Separate groups with spaces.";
      }
    }
    if (this.targetKind === "generation" && !/^[1-9][0-9]*$/.test(this.generation.trim())) {
      errs["target"] = "A positive generation number.";
    }
    return errs;
  }

  /**
   * Choosing a binding fills in only what that binding actually reads on the JWT
   * path — the issuer it verifies against — and leaves the rest to be typed.
   * `SC-S714`'s last criterion is that the synthetic identity cannot succeed via
   * a fact the real path would not see, and the binding's `claims` block holds
   * claim *paths*, not values, so there is nothing there to prefill as a value.
   */
  private chooseBinding(name: string) {
    this.bindingName = name;
    const b = this.bindings.find((x) => x.name === name);
    if (b) this.issuer = b.issuer;
  }

  private async run() {
    const errs = this.validateForm();
    this.fieldErrors = errs;
    if (Object.keys(errs).length > 0) return;

    this.abort?.abort();
    this.abort = new AbortController();
    this.running = true;
    this.runError = null;
    this.result = null;
    this.job = null;
    this.resultTarget = this.targetKind;
    this.ranSql = this.sql;
    try {
      const started = await explain(
        this.instanceId,
        { target: this.target, subject: this.subject, sql: this.sql },
        { waitSeconds: WAIT_SECONDS },
      );
      this.job = started.job;
      const done = isTerminal(started.job.status)
        ? started.job
        : await watchJob<ExplainResult>(started.job.id, {
            waitSeconds: WAIT_SECONDS,
            onUpdate: (j) => (this.job = j),
            signal: this.abort.signal,
          });
      this.job = done;
      const failure = jobFailure(done);
      if (failure) {
        this.runError = failure;
        return;
      }
      this.result = done.result;
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      const parsed = parseError(err);
      this.runError = parsed;
      const fields = validationFields(parsed);
      if (fields.length > 0) {
        this.fieldErrors = Object.fromEntries(fields.map((f) => [f.path, f.reason]));
      }
    } finally {
      this.running = false;
    }
  }

  private async cancel() {
    const id = this.job?.id;
    if (!id) return;
    try {
      await cancelJob(id);
    } catch (err) {
      this.runError = parseError(err);
    }
  }

  /* ── rendering ─────────────────────────────────────────────────────────── */

  override render() {
    if (this.loading && !this.instance) {
      return html`<bn-spinner block size="28" label="Loading this instance"></bn-spinner>`;
    }
    if (this.loadError) return this.renderLoadError(this.loadError);
    return html`
      <header>
        <div class="head-line">
          <h1>SQL playground</h1>
          <bn-badge variant="info">Dry run</bn-badge>
          <bn-badge variant="warning">${JWT_ONLY_BADGE}</bn-badge>
        </div>
        <p class="lede">
          Evaluate a statement against your policies without running it. The real parser reads the
          SQL inside this instance's pinned image, so shape-dependent policies apply exactly as they
          would live — and no database is contacted, because nothing is executed.
        </p>
      </header>
      <div class="stack">
        ${this.renderForm()} ${this.renderRunState()} ${this.renderOutcome()}
        ${this.renderDryRunNote()}
      </div>
    `;
  }

  private renderForm() {
    return html`<bn-card heading="The statement">
      <bn-field
        label="SQL"
        required
        .error=${this.fieldErrors["sql"] ?? ""}
        hint="Parsed by Sluice's own parser. Nothing is sent to a database."
      >
        <bn-code
          language="sql"
          label="Statement to evaluate"
          filename="playground.sql"
          .readonly=${false}
          .value=${this.sql}
          .diagnostics=${this.result?.parse_error && this.sql === this.ranSql
            ? [
                {
                  from: 0,
                  to: this.sql.length,
                  severity: "error" as const,
                  message: this.result.parse_error,
                },
              ]
            : []}
          @bn-code-change=${(e: CustomEvent<{ value: string }>) => (this.sql = e.detail.value)}
        ></bn-code>
      </bn-field>

      ${this.renderSubjectFieldset()}

      <div class="row">
        <bn-field class="narrow" label="Evaluate against">
          <bn-select
            .options=${TARGET_OPTIONS}
            .value=${this.targetKind}
            @change=${(e: CustomEvent<BnSelectChangeDetail>) =>
              (this.targetKind = e.detail.value as "draft" | "live" | "generation")}
          ></bn-select>
        </bn-field>
        ${this.targetKind === "generation"
          ? html`<bn-field label="Generation" required .error=${this.fieldErrors["target"] ?? ""}>
              <bn-input
                mono
                inputmode="numeric"
                .value=${this.generation}
                @input=${(e: CustomEvent<{ value: string }>) => (this.generation = e.detail.value)}
              ></bn-input>
            </bn-field>`
          : nothing}
      </div>

      <div class="actions">
        <bn-button variant="primary" ?loading=${this.running} @click=${() => void this.run()}>
          ${this.running ? "Evaluating…" : "Run dry run"}
        </bn-button>
        ${this.running && this.job
          ? html`<bn-button @click=${() => void this.cancel()}>Cancel</bn-button>`
          : nothing}
        <span class="muted">Returns a decision. It cannot return rows.</span>
      </div>
    </bn-card>`;
  }

  /**
   * The subject. A binding may be picked to borrow its issuer and to be told what
   * that binding reads; an API-key-only binding is offered **disabled**, because
   * explain describes a JWT-shaped identity and simulating that binding would
   * produce a decision the real API-key path does not give (`SLUICE-U27`).
   */
  private renderSubjectFieldset() {
    const b = this.binding;
    return html`<fieldset>
      <legend>The subject</legend>
      ${this.bindings.length > 0
        ? html`<bn-field
            label="Start from a subject binding"
            hint="Borrows the issuer the binding verifies against, and tells you which claim paths it maps. The claim values are still yours to type — a binding stores paths, not values."
          >
            <bn-select
              .options=${this.bindingOptions()}
              .value=${this.bindingName}
              placeholder="Type the subject by hand"
              @change=${(e: CustomEvent<BnSelectChangeDetail>) => this.chooseBinding(e.detail.value)}
            ></bn-select>
          </bn-field>`
        : this.draftRead
          ? html`<p class="muted">
              This draft declares no subject bindings, so the subject below is entered by hand. A
              binding is what turns a real credential into a subject; without one the instance
              authenticates nobody.
            </p>`
          : nothing}
      ${b ? this.renderBindingNotes(b) : nothing}

      <div class="grid">
        <bn-field
          label="Subject id"
          required
          .error=${this.fieldErrors["subject.user"] ?? ""}
          hint="The value the binding's subjectId path would resolve to."
        >
          <bn-input
            .value=${this.user}
            @input=${(e: CustomEvent<{ value: string }>) => (this.user = e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-field label="Issuer">
          <bn-input
            .value=${this.issuer}
            @input=${(e: CustomEvent<{ value: string }>) => (this.issuer = e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-field label="Email">
          <bn-input
            type="email"
            .value=${this.email}
            @input=${(e: CustomEvent<{ value: string }>) => (this.email = e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-field
          label="Groups"
          .error=${this.fieldErrors["subject.groups"] ?? ""}
          hint="Space-separated. A comma is refused — the CLI splits the flag on commas."
        >
          <bn-input
            .value=${this.groupsText}
            @input=${(e: CustomEvent<{ value: string }>) => (this.groupsText = e.detail.value)}
          ></bn-input>
        </bn-field>
      </div>
      <p class="muted">
        Need claims, or a table rather than a statement? The
        <a class="inline" href=${this.explainHref()}>explain surface</a> takes the full synthetic
        subject and shows the whole decision trace.
      </p>
    </fieldset>`;
  }

  /** The picker's options. A binding that only accepts API keys is present so it
   * can be recognised and disabled so it cannot be chosen, with the reason shown
   * rather than hidden — the same discipline the policy editors apply to a field
   * the build does not enforce. */
  private bindingOptions(): BnSelectOption[] {
    const options: BnSelectOption[] = [
      {
        value: "",
        label: "Type the subject by hand",
        description: "No binding — describe the identity yourself.",
      },
    ];
    return options.concat(this.bindings.map((b) => {
      const apiKeyOnly = b.apiKeys && !b.jwt;
      return {
        value: b.name,
        label: b.name,
        description: apiKeyOnly
          ? "API-key mode only."
          : b.issuer
            ? `Issuer ${b.issuer}`
            : "No issuer declared.",
        disabled: apiKeyOnly,
        disabledReason:
          "An API-key subject cannot be simulated: explain and test build a JWT-shaped identity only, so this binding's consumers cannot be previewed here (SLUICE-U27). A live call with a real minted key is the only coverage for that path.",
      };
    }));
  }

  private renderBindingNotes(b: BindingFacts) {
    const paths = Object.entries(b.claimPaths);
    return html`<div>
      ${b.apiKeys && b.jwt
        ? html`<p role="status">
            <strong>This binding accepts API keys as well as JWTs, and only the JWT half is being
            simulated.</strong>
            An API-key subject carries just the literal claims the binding stamps —
            <code>tenantId</code> and <code>allowedRegions</code> — while a JWT subject carries the
            whole claim map. A policy that treats the two differently will not behave here as it does
            for a key consumer (<code>SLUICE-U27</code>).
          </p>`
        : nothing}
      ${paths.length > 0
        ? html`<p class="muted">
            Maps ${paths.map(([k, v]) => html`<span class="chip">${k} ← ${v}</span> `)} — those are
            claim paths. On the JWT path a template reaches a claim as
            <code>{{ subject.claims.&lt;path&gt; }}</code>; <code>tenantId</code> and
            <code>allowedRegions</code> resolve as bare subject fields only in API-key mode.
          </p>`
        : nothing}
    </div>`;
  }

  private renderRunState() {
    const job = this.job;
    if (!job || !this.running) return nothing;
    return html`<bn-panel eyebrow="Dry run in progress">
      <div class="actions">
        <bn-spinner
          label=${job.status === "queued"
            ? "Queued — waiting for a slot on the cluster"
            : "Evaluating on the pinned image"}
        ></bn-spinner>
        <a class="inline" href=${hrefFor("jobs", job.id)}>Job ${job.id}</a>
      </div>
    </bn-panel>`;
  }

  private renderOutcome() {
    if (this.runError) return this.renderRunError(this.runError);
    const r = this.result;
    if (!r) return this.running ? nothing : this.renderNeverRun();
    if (r.parse_error) return this.renderParseError(r);
    if (!r.explain) return nothing;
    return this.renderResult(r);
  }

  private renderNeverRun() {
    return html`<bn-card>
      <bn-empty-state
        icon="⌗"
        heading="Nothing evaluated yet"
        message="Paste a statement and name a subject. You get the decision, the policies that produced it, the tables that would be filtered and the columns that would be masked — and no rows, because nothing runs."
      >
        <bn-button slot="actions" variant="primary" @click=${() => void this.run()}>
          Run dry run
        </bn-button>
      </bn-empty-state>
    </bn-card>`;
  }

  private renderParseError(r: ExplainResult) {
    return html`<bn-card heading="That statement did not parse">
      <div role="alert">
        ${this.renderStaleNote()}
        <p>
          The parser refused the statement, so no policy was evaluated. This is a syntax error and
          <strong>not</strong> a denial — it is not counted as one anywhere.
        </p>
        <bn-kv
          .rows=${[{ label: "Parser message", value: r.parse_error ?? "" }] as BnKvRow[]}
        ></bn-kv>
        <p class="muted">
          The parser reports no position, so the editor marks the whole statement rather than a
          character it never named.
        </p>
      </div>
    </bn-card>`;
  }

  private renderResult(r: ExplainResult) {
    const e = r.explain;
    if (!e) return nothing;
    const facts = DECISIONS[e.decision_kind] ?? DECISIONS.unknown;
    return html`
      <bn-card heading="What your policies would do">
        ${this.renderStaleNote()}
        <div class="verdict">
          <bn-badge .variant=${facts.variant}>${facts.label}</bn-badge>
          <span class="verdict-line">${facts.headline}</span>
        </div>
        <bn-kv
          .rows=${[
            { label: "Subject", value: e.subject },
            { label: "Resource", value: e.resource },
            { label: "Evaluated against", value: targetLabel(r.target), copyable: false },
            ...(r.target.digest ? [{ label: "Bundle digest", value: r.target.digest }] : []),
            { label: "Rows returned", value: String(r.dry_run.rows_returned), copyable: false },
            { label: "Duration", value: `${r.duration_ms} ms`, copyable: false },
            ...(this.job ? [{ label: "Job", value: this.job.id }] : []),
          ] as BnKvRow[]}
        ></bn-kv>
        <p class="muted">${r.dry_run.notice}</p>
      </bn-card>

      <bn-card heading="Filters and masks">
        <div class="section">
          <h2>Tables that would be filtered</h2>
          ${e.effective.row_filters.length === 0
            ? html`<p class="muted">None.</p>`
            : html`<div class="chips">
                  ${e.effective.row_filters.map((t) => html`<span class="chip">${t}</span>`)}
                </div>
                <p class="muted">
                  An unordered <strong>set</strong>: it comes from a map iteration upstream, so the
                  order means nothing. Explain returns no composed predicate, so this says which
                  tables carry a filter and never what the filter is.
                </p>`}
        </div>
        <div class="section">
          <h2>Columns that would be masked</h2>
          ${e.effective.column_masks.length === 0
            ? html`<p class="muted">None.</p>`
            : html`<bn-table
                  caption="Column masks"
                  .columns=${MASK_COLUMNS}
                  .rows=${e.effective.column_masks.map((m) => ({
                    column: m.column,
                    mask_type: m.mask_type,
                    policy: m.policy,
                    stage: this.stageLabel(m.policy),
                  })) as BnRow[]}
                ></bn-table>
                <p class="muted">
                  Mask arguments are not in the payload, so this is which mask claims each column,
                  not what the value becomes. Masks never stack — one policy wins per column.
                </p>`}
          ${this.renderPostQueryWarning(r)}
        </div>
      </bn-card>

      ${this.renderPolicyTable(e)} ${this.renderCaveats(r)}

      <bn-card heading="Take this further">
        <div class="actions">
          <bn-button @click=${() => navigate(this.explainHref())}>
            Open the full decision trace
          </bn-button>
          <bn-button @click=${() => navigate(hrefFor("instances", this.instanceId, "tests"))}>
            Turn this into a test case
          </bn-button>
        </div>
        <p class="muted">
          A dry run answers "what would happen". A stored test case answers "does it still happen",
          on every change, in front of a publish.
        </p>
      </bn-card>
    `;
  }

  /** Says so when the statement has been edited since this answer was produced. */
  private renderStaleNote() {
    if (this.sql === this.ranSql) return nothing;
    return html`<p class="muted" role="status">
      The statement above has been edited since this dry run. Run it again to evaluate what is in the
      editor now.
    </p>`;
  }

  /** The one live failure mode a dry run cannot observe. Explain returns no
   * rewrite effect, so the condition is stated rather than asserted — and the
   * stage comes from the control plane's own computation over the stored mask
   * arguments, never from a guess here. */
  private renderPostQueryWarning(r: ExplainResult) {
    const masks = r.explain?.effective.column_masks ?? [];
    const postQuery = masks.filter((m) => this.maskStages[m.policy] === "post_query");
    const unknown = masks.filter((m) => !(m.policy in this.maskStages));
    if (postQuery.length === 0 && unknown.length === 0) return nothing;
    return html`<div role="status">
      ${postQuery.length > 0
        ? html`<p>
            <strong
              >${postQuery.map((m) => m.column).join(", ")}
              ${postQuery.length === 1 ? "is" : "are"} masked after the query runs.</strong
            >
            A post-query masked column is refused anywhere other than a bare top-level select item:
            put it in a <code>WHERE</code>, a <code>GROUP BY</code>, an <code>ORDER BY</code> or an
            expression and the live query fails with <code>ERR_MASK_UNSUPPORTED_CONTEXT</code>
            instead of returning masked rows. This dry run cannot tell you whether that applies to
            the statement above — explain returns no rewrite effect — so check the statement against
            that rule yourself.
          </p>`
        : nothing}
      ${unknown.length > 0
        ? html`<p class="muted">
            The execution stage of
            ${unknown.map((m) => m.policy).join(", ")} could not be read from this draft
            ${this.resultTarget === "draft"
              ? "— the mask policy is not in it, so it may come from a generation this draft has moved past"
              : "— you evaluated an older generation, and its mask arguments may differ from the draft's"}.
            A post-query mask restricts where the column may appear, so treat the stage as unknown
            rather than as SQL-stage.
          </p>`
        : nothing}
    </div>`;
  }

  private stageLabel(policy: string): TemplateResult {
    const stage = this.maskStages[policy];
    if (stage === "post_query") return html`<bn-badge variant="warning">post-query</bn-badge>`;
    if (stage === "sql") return html`<bn-badge variant="neutral">SQL</bn-badge>`;
    return html`<span class="muted">unknown</span>`;
  }

  /** One table for every policy the decision touched, with the role each played.
   * Shadow policies are labelled as such: they matched and changed nothing, and
   * putting them in an unlabelled list would make a not-yet-enforced policy look
   * enforced. */
  private renderPolicyTable(e: NonNullable<ExplainResult["explain"]>) {
    const rows: BnRow[] = [
      ...e.matched.map((p) => ({
        role: html`<bn-badge variant="enforce">matched</bn-badge>`,
        kind: p.kind,
        name: this.policyLink(p.kind, p.name, p.policy_id),
        priority: p.priority,
      })),
      ...e.shadow.map((p) => ({
        role: html`<bn-badge variant="audit">shadow</bn-badge>`,
        kind: p.kind,
        name: this.policyLink(p.kind, p.name, p.policy_id),
        priority: p.priority,
      })),
      ...e.rejected.map((p) => ({
        role: html`<bn-badge variant="deny">refused</bn-badge>`,
        kind: p.kind,
        name: this.policyLink(p.kind, p.name, p.policy_id),
        priority: html`<span class="muted" title=${p.reason}>${p.reason}</span>`,
      })),
      ...e.approval_required.map((p) => ({
        role: html`<bn-badge variant="info">approval</bn-badge>`,
        kind: p.kind,
        name: this.policyLink(p.kind, p.name, p.policy_id),
        priority: p.priority,
      })),
    ];
    return html`<bn-card heading="Policies in this decision">
      <bn-table
        caption="Policies in this decision"
        empty="No policy matched this subject and resource at all."
        .columns=${POLICY_COLUMNS}
        .rows=${rows}
      ></bn-table>
      <p class="muted">
        A shadow policy runs in Audit or DryRun: it reported and affected nothing. And explain never
        says why a policy <em>failed</em> to match, so a policy you expected and cannot find here is
        a question for the pattern preview in its editor.
      </p>
    </bn-card>`;
  }

  private policyLink(kind: string, name: string, policyId: string | null): TemplateResult {
    if (!policyId) return html`<span>${name}</span>`;
    return html`<a
      class="inline"
      href=${hrefFor("instances", this.instanceId, "policies", kind, name)}
      >${name}</a
    >`;
  }

  private renderCaveats(r: ExplainResult) {
    if (r.caveats.length === 0) return nothing;
    return html`<bn-card heading="What this dry run does not tell you">
      <ul>
        ${r.caveats.map((c) => html`<li>${c}</li>`)}
      </ul>
    </bn-card>`;
  }

  /**
   * The standing statement about what this surface is. It is deliberately a
   * sentence and not a disabled button: an affordance for a capability that does
   * not exist here is exactly the thing `SC-S714`'s third criterion forbids.
   */
  private renderDryRunNote() {
    return html`<bn-panel eyebrow="This surface is a dry run">
      <p>
        No statement is executed, no datasource is contacted and no result row exists — so nothing on
        this page is your data. What you get is the decision your policies would reach.
      </p>
      <p class="muted">
        Running a query for real as a chosen subject is a separate capability: it requires subject
        impersonation, which is a genuine privilege-escalation surface and needs its own grant, a
        step-up check and an audit record on every use. It is not part of this build, and this page
        does not pretend to hold a disabled version of it.
      </p>
    </bn-panel>`;
  }

  /** The explain surface, prefilled with whatever is on this page. */
  private explainHref(): string {
    return (
      hrefFor("instances", this.instanceId, "explain") +
      explainPrefill({
        target: this.target,
        subject: this.subject,
        sql: this.sql || undefined,
      })
    );
  }

  private renderRunError(err: ApiError) {
    const rows: BnKvRow[] = [{ label: "Error code", value: err.code }];
    if (this.job) rows.push({ label: "Job", value: this.job.id });
    if (err.requestId) rows.push({ label: "Request id", value: err.requestId });
    const upstream = err.details?.["upstream"];
    if (typeof upstream === "string") rows.push({ label: "Upstream ask", value: upstream });
    return html`<bn-card heading="The dry run produced no decision">
      <div role="alert">
        <p class="error-message">${err.serverMessage || err.message}</p>
        <bn-kv .rows=${rows}></bn-kv>
        ${err.code === "policy_invalid"
          ? html`<p class="muted">
                The bundle does not compile, so there is nothing to evaluate against. Validation names
                what to fix.
              </p>
              <div class="error-actions">
                <bn-button
                  variant="primary"
                  @click=${() => navigate(hrefFor("instances", this.instanceId, "validation"))}
                >
                  Open validation
                </bn-button>
              </div>`
          : html`<div class="error-actions">
              <bn-button variant="primary" @click=${() => void this.run()}>Try again</bn-button>
            </div>`}
      </div>
    </bn-card>`;
  }

  private renderLoadError(err: ApiError) {
    return html`<bn-card heading="Could not load this instance">
      <div role="alert">
        <p class="error-message">${err.message}</p>
        <bn-kv
          .rows=${[
            { label: "Error code", value: err.code },
            ...(err.requestId ? [{ label: "Request id", value: err.requestId }] : []),
          ] as BnKvRow[]}
        ></bn-kv>
        <div class="error-actions">
          <bn-button variant="primary" @click=${() => void this.load()}>Try again</bn-button>
        </div>
      </div>
    </bn-card>`;
  }
}

/**
 * bindingFacts reads what a `SubjectBinding` declares out of its stored spec.
 *
 * It is defensive on purpose: the spec's shape is the form schema's (frozen
 * interface `I3`) rather than a Go struct this page mirrors, so every field is
 * read as unknown and narrowed. What it is looking for is which **mode** the
 * binding accepts, because that is what decides whether this surface can
 * simulate its consumers at all.
 */
function bindingFacts(p: PolicyObject): BindingFacts {
  const spec = (p.spec ?? {}) as Record<string, unknown>;
  const claims = (spec["claims"] ?? {}) as Record<string, unknown>;
  const claimPaths: Record<string, string> = {};
  for (const [k, v] of Object.entries(claims)) {
    if (typeof v === "string" && v !== "") claimPaths[k] = v;
  }
  const issuer = typeof spec["issuer"] === "string" ? spec["issuer"] : "";
  const jwks = typeof spec["jwksUrl"] === "string" ? spec["jwksUrl"] : "";
  const hmac = typeof spec["hmacSecretRef"] === "string" ? spec["hmacSecretRef"] : "";
  const keys = Array.isArray(spec["apiKeys"]) ? (spec["apiKeys"] as unknown[]) : [];
  return {
    name: p.metadata.name,
    issuer,
    jwt: issuer !== "" || jwks !== "" || hmac !== "",
    apiKeys: keys.length > 0,
    claimPaths,
  };
}

function targetLabel(t: { kind: string; generation?: number }): string {
  if (t.kind === "generation") return `generation ${t.generation ?? "?"}`;
  if (t.kind === "live") return `live generation${t.generation ? ` (${t.generation})` : ""}`;
  return t.generation ? `draft (generation ${t.generation})` : "draft";
}

declare global {
  interface HTMLElementTagNameMap {
    "page-playground": PagePlayground;
  }
}
